import React, {useState, useEffect} from 'react'
import moment from 'moment'
import Isvg from 'react-inlinesvg'
import _ from "lodash"
import styled from "styled-components"

import notificationService from "data/notification"
import ConfirmationModal from "components/modal/confirmation-modal"
import { WithTip } from 'components/tooltip/with-tip'
import { ValidationErrorTable } from "components/modal/modal-styles"

import download from "data/lib/download"
import { local } from 'data/lib/local-json'
import { lookup } from 'data/lib/lookup-field'
import { replace } from 'data/lib/handle-references'

import { Button_s, Colors } from 'styles/app-styles'
import { DownloadIcon } from 'images/icons/tools'


const Download = onClick => (
  <WithTip position="bottom">
    <span>Download Object Trace</span>
    <div id="downloadTooltipTarget" onClick={onClick}>
      <Isvg src={DownloadIcon}/>
    </div>
  </WithTip>
)

const isMultifamily = () => local('property_type', null) == 'Multifamily'

const ObjectInUseModal_s = styled(ConfirmationModal)`
  ${Button_s}:first-child {
    background: ${Colors.white} !important;
    border-color: ${Colors.towerGrey} !important;
    color: ${Colors.blackPearl} !important;
    &:hover {
      background: ${Colors.white};
      color: ${Colors.blackPearl};
    }
  }
`

export const ObjectInUseModal = (props) => {
  const [detail, setDetail] = useState<any>({})
  const [show, setShow] = useState<boolean>(false)
  const onHide = () => setShow(false)
  const onConfirm = () => delete_option_available ? deleteItems(can_delete): onHide()
  const { object, references, alternatives, deleteFn, refreshFn, noReplace, canForce, message, no_delete, can_delete, special, specialId, num_removable, num_not_removable} = detail
  const addPath = ['detailaccount'].includes(object?.table)

  let delete_option_available = num_removable >= 1
  let can_replace = !delete_option_available && (num_not_removable == 1)

  const deleteItems = (items) => {
    deleteFn(items)
    setShow(false)
  }

  useEffect(() => {
    const handle = (event) => {
      setDetail(event.detail)
      setShow(true)
    }
    document.addEventListener("rdcf-object-in-use", handle)

    return function cleanup() {
      document.removeEventListener("rdcf-object-in-use", handle)
    }
  }, [])

  if (show && _.isEmpty(detail.references) && !deleteFn){
    notificationService.notify({
      type: "info",
      message: "No references found",
      dismiss: 3000
    })
    return null
  }

  else if (show && deleteFn && !can_replace) {
    return <ConfirmationModal
      cancelFunc={onHide}
      toggleShow={onHide}
      confirmOnly={!delete_option_available}
      closeOnly={false}
      confirmFunc={onConfirm}
      message={message || ""}
      show={show}
      confirmText={'Ok'}
      cancelText={'Cancel'}
      title={'References found'}
      >
      {no_delete && <div>In Use: {_.map(no_delete, o => <li>{o.code}</li>)}</div>}
      {delete_option_available && <div> <br/> Would you like to delete {num_removable} unused objects? </div>}
      </ConfirmationModal>
  }

  const downloadItems = (object, refs) => {
    const { name, code } = object
    const filename = `${code}_${name}_references_${moment().format('YYYY-MM-DD-HHmm')}.txt`
    const lines = [
      'Location\t' + (isMultifamily() ? 'Code/Unit/Acct#' : 'Code/Suite/Acct#') + '\tColumn',
      ...refs.map(({ section, table, field, column, state, propertypath, item: { code, name, suite, default: d, unitdescription, description } }) =>
        [
          (addPath ? propertypath + ':' : '') + _.startCase(section || table),
          code || suite || unitdescription || description ,
          d || _.startCase(field || column) + (state ? ': ' + _.startCase(state) + ' Input' : ''),
        ].join('\t')
      )
    ]
    download(filename, lines)
  }

  const header = (
    <thead>
      <tr>
        <th>Section</th>
        <th>{isMultifamily() ? 'Code/Unit/Acct#' : 'Code/Suite/Acct#'}</th>
        <th>Name</th>
        <th>Column</th>
      </tr>
    </thead>
  )

  const items = _(references).map((ref) => {
    const { table, paths, field, referencedBy, propertypath } = ref
    const column = referencedBy['default'] || paths[0][0]
    const state = _.get(referencedBy, [column, 'state'])
    let sectionField = lookup(table, column, referencedBy)
    if (!sectionField.field) delete sectionField.field
    return {
      item: _.pick(referencedBy, ['code', 'suite', 'unitdescription', 'description']),
      name: referencedBy.name || referencedBy.tenant,
      table,
      field,
      column,
      state,
      sortOrder: referencedBy.sortorder,
      propertypath,
      ...sectionField
    }
  }).orderBy(['section', 'column', 'item.code', 'item.suite','sortOrder']).value()


  const elements = _.map(items, ({ item, table, section, column, field, name, state, propertypath }, ix) => (
    <tr key={ix}>
      <td>{(addPath ? propertypath + ':' : '') + _.startCase(section || table)} </td>
      <td>
        <span>{_.map(item, (v, k) => v).join(', ')}</span>
      </td>
      <td>
        <span>{name || ''}</span>
      </td>
      <td>{_.startCase(field || column)}{state ? ': ' + _.startCase(state) + ' Input' : ''}</td>
    </tr>
  ))

  const title = [
    deleteFn ?
      canForce ?
        'Delete object and all its references?' :
        'Could not delete item because it is referenced from:' :
      `"${_.get(detail, 'object.code') + (_.get(detail, 'object.name') ? `| ${_.get(detail, 'object.name')}` : '')}" traced to these locations:`
    , Download(() => downloadItems(object, items))]
  let body = [
    <table key="validation-error-table">
      {header}
      <tbody>{elements}</tbody>
    </table>
  ]

  // const itemFn = (item) => (item.name ? `${item.code}: ${item.name}` : item.code)
  const itemFn = (item) => _.filter([item.code, item.name]).join(': ')
  const confirmText = deleteFn ? canForce ? 'Delete' : 'Replace & Delete' : 'Replace'
  const _deleteFn = async (selectRef) => {
    const replacementid = _.get(selectRef, `current.selectedOptions[0].value`)
    if (!replacementid && !canForce) return
    try {
      if (canForce) {
        setShow(false)
        await deleteFn()
        refreshFn && await refreshFn()
        return
      }
      const changes = await replace(object.table, object[`${object.table}id`], replacementid, special, specialId)
      setShow(false)
      deleteFn && await deleteFn()
      if (object.table in changes) {
        refreshFn && await refreshFn()
      }
    }
    catch (e) {
      notificationService.notify({
        type: "warn",
        message: _.get(e,'message'),
        dismiss: 3000
      })
    }
  }

  return (
    <ObjectInUseModal_s
      cancelFunc={onHide}
      toggleShow={onHide}
      confirmOnly={true}
      closeOnly={false}
      confirmFunc={onHide}
      message=""
      show={show}
      confirmText={'Cancel'}
      title={title}
      select={!detail.noReplace}
      selectOptions={!_.isEmpty(alternatives) && _.map([{code:null}].concat(alternatives), it => [it.id, itemFn(it)])}
      selectLabel={'Replace with:'}
      tertiary={!_.isEmpty(alternatives) && (!detail.noReplace || canForce)}
      tertiaryFunc={_deleteFn}
      tertiaryText={confirmText}
      scrollY>
      <ValidationErrorTable>{ body }</ValidationErrorTable>
    </ObjectInUseModal_s>
  )
}